<template>
 <section class="task-panel">
   <div class="task-head">
     <div>
       <h3>伙伴的计划与待办</h3>
       <p class="help">在这里与她约定接下来想做的事。当前仅保存计划，不会自动执行浇水、移动或交易。</p>
     </div>
     <el-button :loading="loading" @click="reload">刷新</el-button>
   </div>
   <el-form label-position="top" class="task-create" @submit.prevent="create">
     <el-form-item label="想一起做什么？">
       <el-input v-model="draft.title" maxlength="120" show-word-limit placeholder="例如：明天一起去海边钓鱼" />
     </el-form-item>
     <el-form-item label="计划类型">
       <el-select v-model="draft.category" style="width:100%">
         <el-option label="农场劳动" value="FARM" />
         <el-option label="探索与娱乐" value="EXPLORE" />
         <el-option label="结伴与社交" value="SOCIAL" />
         <el-option label="日常生活" value="LIFE" />
       </el-select>
     </el-form-item>
     <el-form-item label="补充说明（可选）">
       <el-input v-model="draft.description" maxlength="1000" show-word-limit type="textarea" :rows="2" placeholder="比如：等你有空、有足够体力时再考虑；不要私自动用其他玩家的物品" />
     </el-form-item>
     <div class="create-actions">
       <el-button plain :loading="suggesting" @click="suggest">让 AI 想个主意</el-button>
       <el-button type="primary" :loading="creating" @click="create">保存计划建议</el-button>
     </div>
   </el-form>
   <el-divider />
   <div v-if="!tasks.length" class="empty">现在没有待办。可以先和伙伴聊聊，再在这里记录共同计划。</div>
   <div v-for="task in tasks" :key="task.id" class="task-item">
     <div class="task-row">
       <strong>{{ task.title }}</strong>
       <el-tag size="small" :type="task.status==='PLANNED'?'success':task.status==='CANCELLED'?'info':task.status==='COMPLETED'?'success':'warning'">
         {{statusLabels[task.status]||task.status}}
       </el-tag>
     </div>
     <div class="task-meta">{{categoryLabels[task.category]||task.category}} · {{task.origin==='WEB_REQUEST'?'管理端提出':'其他来源'}}</div>
     <p v-if="task.description" class="task-details">{{task.description}}</p>
     <div v-if="task.status==='PROPOSED'||task.status==='PLANNED'" class="task-buttons">
       <el-button v-if="task.status==='PROPOSED'" size="small" plain type="primary" :loading="processingId===task.id" @click="change(task,'plan')">加入待办计划</el-button>
       <el-button size="small" plain :loading="processingId===task.id" @click="change(task,'cancel')">取消</el-button>
     </div>
   </div>
   <p class="help footnote">“列入计划”不等于任务已完成。执行进度需要后续游戏端提供可信回执才能更新，页面不会人为标记成功。</p>
 </section>
</template>

<script setup>
import {onMounted,reactive,ref} from 'vue'
import {ElMessage} from 'element-plus'
import {http} from '../plugins/axios'
import {authErrorMessage} from '../auth'
const tasks=ref([]),loading=ref(false),creating=ref(false),suggesting=ref(false),processingId=ref(null)
const draft=reactive({title:'',category:'FARM',description:''})
const statusLabels={PROPOSED:'等待确认',PLANNED:'已列入计划',CANCELLED:'已取消',COMPLETED:'游戏已确认完成'}
const categoryLabels={FARM:'农场劳动',EXPLORE:'探索与娱乐',SOCIAL:'结伴与社交',LIFE:'日常生活'}
async function reload(){
 loading.value=true
 try{
  const {data}=await http.get('/api/manage/ai/tasks')
  tasks.value=Array.isArray(data)?data:[]
 }catch(e){ElMessage.error(authErrorMessage(e,'计划读取失败'))}
 finally{loading.value=false}
}
async function suggest(){
 if(suggesting.value)return
 suggesting.value=true
 try{
  const {data}=await http.post('/api/manage/ai/tasks/suggest',{
   category:draft.category,hint:draft.description.slice(0,250)
  },{timeout:55000})
  if(data?.suggestion){
   draft.title=data.suggestion
   ElMessage.success('AI 提了一个建议，请看看是否合适，再选择保存')
  }
 }catch(e){ElMessage.error(authErrorMessage(e,'AI 暂时没有想出建议'))}
 finally{suggesting.value=false}
}
async function create(){
 if(creating.value)return
 if(draft.title.trim().length<3){ElMessage.warning('请写下至少三个字的计划');return}
 creating.value=true
 try{
  await http.post('/api/manage/ai/tasks',{title:draft.title.trim(),category:draft.category,description:draft.description.trim()})
  draft.title='';draft.description=''
  ElMessage.success('已记录计划建议，目前没有执行游戏操作')
  await reload()
 }catch(e){ElMessage.error(authErrorMessage(e,'计划保存失败'))}
 finally{creating.value=false}
}
async function change(task,operation){
 if(processingId.value!==null)return
 processingId.value=task.id
 try{
  const {data}=await http.post(`/api/manage/ai/tasks/${task.id}/${operation}`)
  ElMessage.success(data?.message||'操作成功')
  await reload()
 }catch(e){ElMessage.error(authErrorMessage(e,'计划更新失败'))}
 finally{processingId.value=null}
}
onMounted(reload)
</script>

<style scoped>
.task-panel{margin-top:20px;padding:27px;border:1px solid #e5ece7;border-radius:14px;background:white}
.task-head{display:flex;align-items:flex-start;justify-content:space-between;gap:12px}
.task-head h3{font-size:17px;margin:0}.help{color:#77897a;font-size:13px;line-height:1.8;margin:6px 0 16px}
.task-create{margin-top:15px;padding:17px 19px;background:#f8faf5;border-radius:12px}
.empty{background:#f8faf5;border-radius:9px;padding:19px;font-size:13px;color:#718374}
.task-item{padding:14px 0;border-bottom:1px solid #e8ede8}
.task-row{display:flex;justify-content:space-between;align-items:center;gap:12px;font-size:14px;color:#45624a}
.task-meta{font-size:12px;color:#87998c;margin-top:6px}
.task-details{font-size:13px;color:#657469;white-space:pre-wrap;line-height:1.7}
.task-buttons{display:flex;gap:8px;margin-top:10px}.footnote{margin-top:18px}.create-actions{display:flex;flex-wrap:wrap;gap:8px;align-items:center}
@media(max-width:560px){.task-panel{padding:18px}.task-head{flex-wrap:wrap}.task-create{padding:12px}}
</style>
